"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var Node_1 = require("../snap/Node");
var Change_1 = require("./Change");
var util_1 = require("@firebase/util");
/**
 * An EventGenerator is used to convert "raw" changes (Change) as computed by the
 * CacheDiffer into actual events (Event) that can be raised. See generateEventsForChanges()
 * for details.
 *
 * @constructor
 */
var EventGenerator = /** @class */ (function () {
 /**
 *
 * @param {!Query} query_
 */
 function EventGenerator(query_) {
 this.query_ = query_;
 /**
 * @private
 * @type {!Index}
 */
 this.index_ = this.query_.getQueryParams().getIndex();
 }
 /**
 * Given a set of raw changes (no moved events and prevName not specified yet), and a set of
 * EventRegistrations that should be notified of these changes, generate the actual events to be raised.
 *
 * Notes:
 * - child_moved events will be synthesized at this time for any child_changed events that affect
 * our index.
 * - prevName will be calculated based on the index ordering.
 *
 * @param {!Array.} changes
 * @param {!Node} eventCache
 * @param {!Array.} eventRegistrations
 * @return {!Array.}
 */
 EventGenerator.prototype.generateEventsForChanges = function (changes, eventCache, eventRegistrations) {
 var _this = this;
 var events = [];
 var moves = [];
 changes.forEach(function (change) {
 if (change.type === Change_1.Change.CHILD_CHANGED &&
 _this.index_.indexedValueChanged(change.oldSnap, change.snapshotNode)) {
 moves.push(Change_1.Change.childMovedChange(change.childName, change.snapshotNode));
 }
 });
 this.generateEventsForType_(events, Change_1.Change.CHILD_REMOVED, changes, eventRegistrations, eventCache);
 this.generateEventsForType_(events, Change_1.Change.CHILD_ADDED, changes, eventRegistrations, eventCache);
 this.generateEventsForType_(events, Change_1.Change.CHILD_MOVED, moves, eventRegistrations, eventCache);
 this.generateEventsForType_(events, Change_1.Change.CHILD_CHANGED, changes, eventRegistrations, eventCache);
 this.generateEventsForType_(events, Change_1.Change.VALUE, changes, eventRegistrations, eventCache);
 return events;
 };
 /**
 * Given changes of a single change type, generate the corresponding events.
 *
 * @param {!Array.} events
 * @param {!string} eventType
 * @param {!Array.} changes
 * @param {!Array.} registrations
 * @param {!Node} eventCache
 * @private
 */
 EventGenerator.prototype.generateEventsForType_ = function (events, eventType, changes, registrations, eventCache) {
 var _this = this;
 var filteredChanges = changes.filter(function (change) { return change.type === eventType; });
 filteredChanges.sort(this.compareChanges_.bind(this));
 filteredChanges.forEach(function (change) {
 var materializedChange = _this.materializeSingleChange_(change, eventCache);
 registrations.forEach(function (registration) {
 if (registration.respondsTo(change.type)) {
 events.push(registration.createEvent(materializedChange, _this.query_));
 }
 });
 });
 };
 /**
 * @param {!Change} change
 * @param {!Node} eventCache
 * @return {!Change}
 * @private
 */
 EventGenerator.prototype.materializeSingleChange_ = function (change, eventCache) {
 if (change.type === 'value' || change.type === 'child_removed') {
 return change;
 }
 else {
 change.prevName = eventCache.getPredecessorChildName(
 /** @type {!string} */
 change.childName, change.snapshotNode, this.index_);
 return change;
 }
 };
 /**
 * @param {!Change} a
 * @param {!Change} b
 * @return {number}
 * @private
 */
 EventGenerator.prototype.compareChanges_ = function (a, b) {
 if (a.childName == null || b.childName == null) {
 throw util_1.assertionError('Should only compare child_ events.');
 }
 var aWrapped = new Node_1.NamedNode(a.childName, a.snapshotNode);
 var bWrapped = new Node_1.NamedNode(b.childName, b.snapshotNode);
 return this.index_.compare(aWrapped, bWrapped);
 };
 return EventGenerator;
}());
exports.EventGenerator = EventGenerator;

//# sourceMappingURL=EventGenerator.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/view/EventGenerator.js
// module id = 94
// module chunks = 0